<template>
  <div class="qa-qc-main well-page">

    <TabularLoaders v-if="showLoader" />
    
    <div v-if="allMappedSamples.length > 0" class="qaqc-main-container">
      <div class="qaqc-top-indicators qaqc-top-indicators-with-context">
        <div class="qaqc-top-indicators-left">
          <div
            v-if="showWellContext || hasReportContext"
            class="qaqc-context-block"
          >
            <div v-if="showWellContext" class="qaqc-context-pair">
              <span class="qaqc-context-title">Well:</span>
              <span class="qaqc-context-value">{{ displayWellName }}</span>
            </div>
            <div v-if="hasReportContext" class="qaqc-context-pair">
              <span class="qaqc-context-title">Report:</span>
              <span class="qaqc-context-value">{{ displayReportName }}</span>
            </div>
          </div>
        </div>
        <div class="qaqc-top-indicators-legend">
          <div class="qaqc-indicator-item">
            <div class="qaqc-indicator-dot passed"></div>
            <span>Passed</span>
          </div>
          <div class="qaqc-indicator-item">
            <div class="qaqc-indicator-dot failed"></div>
            <span>Failed</span>
          </div>
          <div class="qaqc-indicator-item">
            <div class="qaqc-indicator-dot pending"></div>
            <span>Pending</span>
          </div>
        </div>
      </div>
      <div class="qaqc-table-section">
        <div class="qaqc-table-content qaqc-detail-table-card">
          <v-table class="test-data-table qaqc-table-wrap qaqc-scorecard-data-table qaqc-main-table qaqc-detail-samples-table">
            <thead>
              <tr class="main-header qaqc-table-header">
                <th class="qaqc-main-header text-left">{{ columnDisplayNames?.fluidSampleID || 'Fluid Sample ID' }}</th>
                <th class="qaqc-main-header text-left">Tests</th>
                <th class="qaqc-main-header text-left">{{ columnDisplayNames?.asset || 'Asset' }}</th>
                <th class="qaqc-main-header text-left">{{ columnDisplayNames?.field || 'Field' }}</th>
                <th class="qaqc-main-header text-left">{{ columnDisplayNames?.reservoir || 'Reservoir' }}</th>
                <th class="qaqc-main-header text-left">{{ 'Report No.' }}</th>
                <th class="qaqc-main-header text-left">{{ columnDisplayNames?.fluidType || 'Fluid Type' }}</th>
                <th class="qaqc-main-header text-left">{{ columnDisplayNames?.fluidClassification || 'Fluid Classification' }}</th>
                <th class="qaqc-main-header text-left">{{ columnDisplayNames?.reservoirPressure || 'Reservoir Pressure' }}</th>
                <th class="qaqc-main-header text-left">{{ columnDisplayNames?.reservoirTemperature || 'Reservoir Temperature' }}</th>
              </tr>
            </thead>
            <tbody class="well-table-content">
              <template v-for="(item, index) in data" :key="index">
                <tr class="qaqc-table-row">
                  <td class="text-left qaqc-detail-body-cell">
                    <span class="qaqc-detail-fluid-chip">{{ item.fluidSampleID }}</span>
                  </td>
                  <td class="text-left qaqc-detail-body-cell qaqc-detail-tests-cell">
                    <div class="qaqc-tests-container">
                      <span
                        v-for="(test, testIndex) in item.testsArray"
                        :key="testIndex"
                        :class="getTestStatusClass(test, item)"
                        @click="handleTestClick(test, item)"
                      >
                        {{ test.displayName }}
                      </span>
                    </div>
                  </td>
                  <td class="text-left qaqc-detail-body-cell">{{ item.asset }}</td>
                  <td class="text-left qaqc-detail-body-cell">{{ item.field }}</td>
                  <td class="text-left qaqc-detail-body-cell">{{ item.reservoir }}</td>
                  <td class="text-left qaqc-detail-body-cell">{{ item.reportIdentifier }}</td>
                  <td class="text-left qaqc-detail-body-cell">{{ item.fluidType }}</td>
                  <td class="text-left qaqc-detail-body-cell">{{ item.fluidClassification }}</td>
                  <td class="text-left qaqc-detail-body-cell">{{ formatValueWithUOM(item.reservoirPressure, 'reservoirPressure', item.originalSample) }}</td>
                  <td class="text-left qaqc-detail-body-cell">{{ formatValueWithUOM(item.reservoirTemperature, 'reservoirTemperature', item.originalSample) }}</td>
                </tr>
              </template>
            </tbody>
          </v-table>
        </div>
      </div>
      <div v-if="totalItems > 0" class="qaqc-detail-pagination-wrap">
        <Pagination
          variant="scorecard"
          :total-items="totalItems"
          v-model:current-page="currentPage"
          v-model:items-per-page="itemsPerPage"
          item-label="Samples"
          :page-sizes="[10, 20, 50, 100]"
          @update:current-page="onDetailPaginationPage"
          @update:items-per-page="onDetailPaginationItemsPerPage"
        />
      </div>
     </div>

    <div v-if="allMappedSamples.length === 0 && !showLoader && timeoutMessage === ''" class="no-asset-div">
      <div class="text-center">
        <v-icon color="grey" size="large" class="mb-2">mdi-database-off</v-icon>
        <h4>No data found</h4>
        <p>There is no data available for QA/QC review at this time.</p>
      </div>
    </div>

    <div v-else-if="timeoutMessage !== ''" class="no-asset-div timeout">
      <div class="text-center">
        <v-icon color="warning" size="large" class="mb-2">mdi-alert-circle</v-icon>
        <p>{{ timeoutMessage }}</p>
        <v-btn color="primary" @click="fetchData()" prepend-icon="mdi-reload">
          Retry
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../axiosInterceptor";
import TabularLoaders from "../../components/layout/Loaders/TabularLoaders.vue";
import Pagination from "../../components/layout/Common/Pagination.vue";
import { getDisplayName } from "../../utils/cache.js";
import {
  parseSamplesListQueryFromRouteQuery,
  appendSamplesApiQueryParams,
  pickSamplesListQueryForRouter,
} from "../../components/layout/QAQC/Overview/qaqcSamplesListQuery.js";
import {
  mergeOverviewTabIntoQuery,
  overviewTabFromTestRoute,
} from "../../components/layout/QAQC/Overview/qaqcOverviewTab.js";

export default {
  name: "QAQCDetailView",
  components: {
    TabularLoaders,
    Pagination,
  },
  data() {
    return {
      activeTab: 'Tests',
      data: [],
      showLoader: false,
      timeoutMessage: "",
      currentPage: 1,
      itemsPerPage: Number(localStorage.getItem("itemsPerPage")) || 20,
      totalItems: 0,
      allMappedSamples: [],
      wellName: null,
      reportFilter: null,
      fluidSampleFilter: null,
      sampleIdFilter: null,
      apiListSelectedTests: null,
      apiListFluidType: null,
      columnDisplayNames: {
        fluidSampleID: 'Fluid Sample ID',
        asset: 'Asset',
        field: 'Field',
        reservoir: 'Reservoir',
        reportIdentifier: 'Report No.',
        fluidType: 'Fluid Type',
        fluidClassification: 'Fluid Classification',
        reservoirPressure: 'Reservoir Pressure',
        reservoirTemperature: 'Reservoir Temperature'
      },
      columnMetadata: {},
    };
  },
  computed: {
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.totalItems);
    },
    displayWellName() {
      return this.wellName || "—";
    },
    hasReportContext() {
      return this.reportFilter != null && String(this.reportFilter).trim() !== "";
    },
    displayReportName() {
      if (!this.hasReportContext) {
        return "—";
      }
      return String(this.reportFilter);
    },
    showWellContext() {
      if (!this.wellName || String(this.wellName).trim() === "") return false;
      if (this.hasReportContext || this.isSampleSelectionContext) return false;
      return true;
    },
    isSampleSelectionContext() {
      if (this.$route?.name === "QAQCDetailSample") return true;
      const f = this.fluidSampleFilter != null && String(this.fluidSampleFilter).trim() !== "";
      const s = this.sampleIdFilter != null && String(this.sampleIdFilter).trim() !== "";
      return f || s;
    },
  },

  methods: {
    goBack() {
      this.$router.go(-1);
    },
    
    async loadColumnDisplayNames() {
      try {
        const columnKeys = [
          'fluidSampleID',
          'asset',
          'field', 
          'reservoir',
          'reportIdentifier',
          'fluidType',
          'fluidClassification',
          'reservoirPressure',
          'reservoirTemperature'
        ];
        
        for (const key of columnKeys) {
          try {
            const result = await getDisplayName('sample', key);
            if (result && result.displayName) {
              this.columnDisplayNames[key] = result.displayName;
              this.columnMetadata[key] = {
                displayName: result.displayName,
                decimals: result.digits || 0
              };
            } else {
              this.columnDisplayNames[key] = this.formatHeaderName(key);
              this.columnMetadata[key] = {
                displayName: this.formatHeaderName(key),
                decimals: 0
              };
            }
          } catch (error) {
            this.columnDisplayNames[key] = this.formatHeaderName(key);
            this.columnMetadata[key] = {
              displayName: this.formatHeaderName(key),
              decimals: 0
            };
          }
        }
      } catch (error) {
        const columnKeys = [
          'fluidSampleID',
          'asset',
          'field', 
          'reservoir',
          'reportIdentifier',
          'fluidType',
          'fluidClassification',
          'reservoirPressure',
          'reservoirTemperature'
        ];
        
        columnKeys.forEach(key => {
          if (!this.columnDisplayNames[key]) {
            this.columnDisplayNames[key] = this.formatHeaderName(key);
            this.columnMetadata[key] = {
              displayName: this.formatHeaderName(key),
              decimals: 0
            };
          }
        });
      }
    },
    
    getUnitForProperty(key, dataSource) {
      const uomKey = key + '_UOM';
      if (dataSource && dataSource[uomKey]) {
        return dataSource[uomKey];
      }
      
      return "";
    },
    
    formatHeaderName(key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    },

    parseWellFromRoute(route) {
      const raw = route.params?.well;
      if (raw != null && String(raw).trim() !== "") {
        const s = String(raw);
        try {
          return decodeURIComponent(s);
        } catch {
          return s;
        }
      }
      const q = route.query?.well;
      if (q != null && String(q).trim() !== "") {
        return String(q);
      }
      return null;
    },

    decodeRouteParam(val) {
      if (val == null || String(val).trim() === "") return null;
      try {
        return decodeURIComponent(String(val));
      } catch {
        return String(val);
      }
    },

    parseSampleFiltersFromRoute(route) {
      const name = route.name;
      const p = route.params || {};
      if (name === "QAQCDetailSample") {
        return {
          reportIdentifier: null,
          fluidSampleID: this.decodeRouteParam(p.fluidSampleID),
          sampleId: this.decodeRouteParam(p.sampleId),
        };
      }
      if (name === "QAQCDetailReport") {
        return {
          reportIdentifier: this.decodeRouteParam(p.reportIdentifier),
          fluidSampleID: null,
          sampleId: this.decodeRouteParam(p.sampleId),
        };
      }
      const q = route.query || {};
      const pick = (key) => {
        const v = q[key];
        return v != null && String(v).trim() !== "" ? String(v) : null;
      };
      return {
        reportIdentifier: pick("reportIdentifier"),
        fluidSampleID: pick("fluidSampleID"),
        sampleId: pick("sampleId"),
      };
    },

    syncSampleFiltersFromRoute(route) {
      const f = this.parseSampleFiltersFromRoute(route);
      this.reportFilter = f.reportIdentifier;
      this.fluidSampleFilter = f.fluidSampleID;
      this.sampleIdFilter = f.sampleId;
      const listQ = parseSamplesListQueryFromRouteQuery(route.query || {});
      this.apiListSelectedTests = listQ.selectedTests;
      this.apiListFluidType = listQ.fluidType;
    },
    
    formatValueWithUOM(value, key, dataSource) {
      if (value === null || value === undefined || value === '-') {
        return value;
      }
      
      const metadata = this.columnMetadata?.[key];
      const decimals = metadata ? metadata.decimals : 0;
      const uom = this.getUnitForProperty(key, dataSource);
      
      let formattedValue = value;
      if (typeof value === 'number' || !isNaN(parseFloat(value))) {
        formattedValue = parseFloat(value).toFixed(decimals);
      }
      
      if (uom) {
        return `${formattedValue} ${uom}`;
      }
      
      return formattedValue;
    },
    onTabChange() {
      this.currentPage = 1;
      this.fetchData();
    },
    onDetailPaginationPage() {
      this.applyClientPagination();
    },
    onDetailPaginationItemsPerPage() {
      this.currentPage = 1;
      localStorage.setItem("itemsPerPage", String(this.itemsPerPage));
      this.applyClientPagination();
    },
    applyClientPagination() {
      const n = this.allMappedSamples.length;
      if (n === 0) {
        this.data = [];
        return;
      }
      const perPage = Math.max(1, this.itemsPerPage);
      const totalPages = Math.max(1, Math.ceil(n / perPage));
      if (this.currentPage > totalPages) {
        this.currentPage = totalPages;
      }
      const start = this.startIndex;
      const end = Math.min(start + perPage, n);
      this.data = this.allMappedSamples.slice(start, end);
    },
    async fetchData() {
      this.showLoader = true;
      this.timeoutMessage = "";
      
      try {
        await this.loadColumnDisplayNames();
        
        if (this.wellName) {
          await this.fetchWellSamples();
        } else {
          this.data = [];
          this.allMappedSamples = [];
          this.totalItems = 0;
        }
      } catch (error) {
        this.timeoutMessage = "Failed to load data. Please try again.";
        this.data = [];
        this.allMappedSamples = [];
        this.totalItems = 0;
      } finally {
      this.showLoader = false;
      }
    },
    async fetchWellSamples() {
      try {
        let url = `/public/api/samples?well="${this.wellName}"&viewSummary=true`;
        if (this.reportFilter) {
          url += `&reportIdentifier="${this.reportFilter}"`;
        }
        if (this.fluidSampleFilter) {
          url += `&fluidSampleID="${this.fluidSampleFilter}"`;
        }
        if (this.sampleIdFilter) {
          url += `&sampleId="${this.sampleIdFilter}"`;
        }
        url = appendSamplesApiQueryParams(url, {
          // selectedTests: this.apiListSelectedTests,
          fluidType: this.apiListFluidType,
        });
        const response = await api.get(url);

        if (response.data && response.data.data && response.data.data.samples && response.data.data.samples.length > 0) {
          this.mapApiDataToTable(response.data.data.samples);
          this.totalItems = this.allMappedSamples.length;
        } else {
          this.data = [];
          this.allMappedSamples = [];
          this.totalItems = 0;
        }
      } catch (error) {
        throw error;
      }
    },
    mapApiDataToTable(apiData) {
      const mappedData = [];
      
      apiData.forEach((sample, index) => {
        const testsArray = this.extractTestsFromSample(sample);
        
        const mappedItem = {
          id: sample.ID || index + 1,
          fluidSampleID: sample.fluidSampleID || sample.uniqueID || `SMP-${index + 1}`,
          testsArray: testsArray,
          asset: sample.location?.asset || '-',
          field: sample.location?.field || '-',
          reservoir: sample.location?.reservoir || '-',
          reportIdentifier: sample.reportIdentifier || sample.ReportIdentifier || '-',
          fluidType: sample.fluidType || '-',
          fluidClassification: this.getFluidClassification(sample),
          reservoirPressure: this.getReservoirPressure(sample),
          testTemperature: this.getTestTemperature(sample),
          reservoirTemperature: this.getReservoirTemperature(sample),
          tTestSelected: false,
          tResSelected: false,
          qcStatus: this.getQCStatus(sample),
          originalSample: sample
        };
        
        mappedData.push(mappedItem);
      });

      this.allMappedSamples = mappedData;
      this.applyClientPagination();
    },
    extractTestsFromSample(sample) {
      const tests = [];
      
      if (sample.compositiontests && sample.compositiontests.length > 0) {
        sample.compositiontests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'Composition',
            testType: 'compositiontests',
            TestType: 'compositiontests', 
            test_type: 'compositiontests', 
            testId: testId,
            ID: testId, 
            id: testId, 
            testData: test,
            displayName: this.getCompositionDisplayName(test, sample.compositiontests.length, index),
            index: index,
            contaminationType: test.contaminationType,
            recombinationType: test.recombinationType,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      
      if (sample.ccetests && sample.ccetests.length > 0) {
        sample.ccetests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'CCE Test',
            testType: 'ccetests',
            TestType: 'ccetests',
            test_type: 'ccetests',
            testId: testId,
            ID: testId,
            id: testId,
            testData: test,
            displayName: this.getTestDisplayName(test, 'CCE', sample.ccetests.length, index),
            index: index,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      
      if (sample.viscositytests && sample.viscositytests.length > 0) {
        sample.viscositytests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'Viscosity Test',
            testType: 'viscositytests',
            TestType: 'viscositytests',
            test_type: 'viscositytests',
            testId: testId,
            ID: testId,
            id: testId,
            testData: test,
            displayName: this.getTestDisplayName(test, 'Viscosity Test', sample.viscositytests.length, index),
            index: index,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      if (sample.dltests && sample.dltests.length > 0) {
        sample.dltests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'DL Test',
            testType: 'dltests',
            TestType: 'dltests',
            test_type: 'dltests',
            testId: testId,
            ID: testId,
            id: testId,
            testData: test,
            displayName: this.getTestDisplayName(test, 'DL', sample.dltests.length, index),
            index: index,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      if (sample.cvdtests && sample.cvdtests.length > 0) {
        sample.cvdtests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'CVD Test',
            testType: 'cvdtests',
            TestType: 'cvdtests',
            test_type: 'cvdtests',
            testId: testId,
            ID: testId,
            id: testId,
            testData: test,
            displayName: this.getTestDisplayName(test, 'CVD', sample.cvdtests.length, index),
            index: index,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      
      if (sample.separatortests && sample.separatortests.length > 0) {
        sample.separatortests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'Separator Test',
            testType: 'separatortests',
            TestType: 'separatortests',
            test_type: 'separatortests',
            testId: testId,
            ID: testId,
            id: testId,
            testData: test,
            displayName: this.getTestDisplayName(test, 'Separator Test', sample.separatortests.length, index),
            index: index,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      if (sample.IsTestAvailable) {
        const testId = sample.ID || sample._id || sample.id;
        tests.push({
          type: 'Sample Validation',
          testType: 'sampleValidation',
          TestType: 'sampleValidation',
          test_type: 'sampleValidation',
          testId: testId,
          ID: testId,
          id: testId,
          testData: sample,
          displayName: 'Sample Validation',
          index: 0
        });
      }
      if (tests.length === 0) {
        const testId = sample.ID || sample._id || sample.id;
        tests.push({
          type: 'Sample Validation',
          testType: 'sampleValidation',
          TestType: 'sampleValidation',
          test_type: 'sampleValidation',
          testId: testId,
          ID: testId,
          id: testId,
          testData: sample,
          displayName: 'Sample Validation',
          index: 0
        });
      }
      
      return tests;
    },
    
    getCompositionDisplayName(test, totalCount, index) {
      let displayName = 'Composition';
      
      if (test.contaminationType || test.recombinationType) {
        displayName += ' (';
        if (test.contaminationType) {
          displayName += test.contaminationType;
        } else if (test.recombinationType) {
          displayName += test.recombinationType;
        }
        displayName += ')';
      }
      
      if (totalCount > 1) {
        displayName += ` (Test #${index + 1})`;
      }
      
      return displayName;
    },
    
    getTestDisplayName(test, testType, totalCount, index) {
      let displayName = test.name || testType;
      
      if (test.testTemperature) {
        const uom = test.testTemperature_UOM || '';
        displayName += ` (${test.testTemperature}${uom ? ` ${uom}` : ''})`;
      } else if (totalCount > 1) {
        displayName += ` (Test #${index + 1})`;
      }
      
      return displayName;
    },
    
    getFluidClassification(sample) {
      if (sample.fluidType === 'Oil') {
        return 'Oil';
      } else if (sample.fluidType === 'Gas') {
        return 'Gas';
      } else if (sample.fluidType) {
        return sample.fluidType;
      }
      return '-';
    },
    getReservoirPressure(sample) {
      if (sample.sampleProperties?.reservoirPressure) {
        return sample.sampleProperties.reservoirPressure;
      }
      return '-';
    },
    getTestTemperature(sample) {
      if (sample.testTemperature != null) {
        return sample.testTemperature;
      }
      if (sample.sampleProperties?.testTemperature != null) {
        return sample.sampleProperties.testTemperature;
      }
      if (sample.standardTemperature != null) {
        return sample.standardTemperature;
      }
      return '-';
    },
    getReservoirTemperature(sample) {
      if (sample.reservoirTemperature) {
        return sample.reservoirTemperature;
      }
      if (sample.sampleProperties?.reservoirTemperature) {
        return sample.sampleProperties.reservoirTemperature;
      }
      if (sample.standardTemperature) {
        return sample.standardTemperature;
      }
      return '-';
    },
    getQCStatus(sample) {
      if (sample.compositiontests && sample.compositiontests.length > 0) {
        return 'Passed';
      }
      return 'Pending';
    },
     
     handleTestClick(test, item) {
       const sampleId = item.originalSample?._id || item.originalSample?.ID || item.id;
       const testId = test.testId;
       
       const testTypeMap = {
         'CCE Test': 'ccetest',
         'DL Test': 'dltest', 
         'CVD Test': 'cvdtest',
         'Viscosity Test': 'viscositytest',
         'Separator Test': 'separatortest',
         'Composition': 'compositiontest',
         'Sample Validation': 'samplevalidation'
       };
       
       const testType = testTypeMap[test.type] || 'ccetest';
       
       const nav = {
         name: "UnifiedTestView",
         params: {
           sampleId: String(sampleId),
           testType: testType,
           testId: String(testId),
         },
       };
       const q = { ...pickSamplesListQueryForRouter(this.$route.query || {}) };
       if (this.wellName) {
         q.well = this.wellName;
       }
       if (this.reportFilter) {
         q.reportIdentifier = this.reportFilter;
       }
       if (item.fluidSampleID != null && String(item.fluidSampleID).trim() !== "") {
         q.fluidSampleID = String(item.fluidSampleID);
       }
       q.sampleId = String(sampleId);
       if (this.$route.name === "QAQCDetailSample" || (this.isSampleSelectionContext && !this.hasReportContext)) {
         q.qaqcCtx = "sample";
       } else if (this.hasReportContext || this.$route.name === "QAQCDetailReport") {
         q.qaqcCtx = "report";
       } else if (this.wellName) {
         q.qaqcCtx = "well";
       } else {
         q.qaqcCtx = "direct";
       }
       nav.query = mergeOverviewTabIntoQuery(
         q,
         overviewTabFromTestRoute({
           params: { testType },
           query: q,
         })
       );
       this.$router.push(nav);
     },
     
    getQCStatusColor(status) {
      const statusColors = {
        'Passed': 'success',
        'Pending': 'warning'
      };
      return statusColors[status] || 'grey';
    },
    getTestStatusClass(test, item) {
      const sample = item.originalSample;
      if (!sample) {
        return 'test-status-pending clickable-test';
      }
      
      if (test.type === 'Sample Validation') {
        if (sample.IsTestAvailable) {
          return 'test-status-passed clickable-test';
        } else {
          return 'test-status-pending clickable-test';
        }
      }
      
      if (test.testData && test.testData.hasOwnProperty('IsVerified')) {
        const isVerifiedValue = test.testData.IsVerified;
        
        if (isVerifiedValue === true || isVerifiedValue === 1 || isVerifiedValue === '1') {
          return 'test-status-passed clickable-test';
        } else if (isVerifiedValue === false || isVerifiedValue === 0 || isVerifiedValue === '0') {
          return 'test-status-failed clickable-test';
        } else {
          return 'test-status-pending clickable-test';
        }
      }
      
      return 'test-status-pending clickable-test';
    }
  },
  watch: {
    '$route'(to, from) {
      const nextWell = this.parseWellFromRoute(to);
      const prevWell = from ? this.parseWellFromRoute(from) : null;
      const nextF = this.parseSampleFiltersFromRoute(to);
      const prevF = from ? this.parseSampleFiltersFromRoute(from) : null;
      const nextList = parseSamplesListQueryFromRouteQuery(to.query || {});
      const prevList = from ? parseSamplesListQueryFromRouteQuery(from.query || {}) : { selectedTests: null, fluidType: null };

      this.wellName = nextWell;
      this.syncSampleFiltersFromRoute(to);

      const listFiltersChanged =
        JSON.stringify(nextList.selectedTests || []) !== JSON.stringify(prevList.selectedTests || []) ||
        String(nextList.fluidType || "") !== String(prevList.fluidType || "");
      const filtersChanged =
        nextF.reportIdentifier !== prevF.reportIdentifier ||
        nextF.fluidSampleID !== prevF.fluidSampleID ||
        nextF.sampleId !== prevF.sampleId ||
        listFiltersChanged;
      const routeVariantChanged = from && to.name !== from.name;

      if (nextWell !== prevWell || filtersChanged || routeVariantChanged) {
        this.currentPage = 1;
        if (this.wellName) {
          this.fetchData();
        } else {
          this.data = [];
          this.allMappedSamples = [];
          this.totalItems = 0;
        }
      }
    }
  },
  mounted() {
    this.wellName = this.parseWellFromRoute(this.$route);
    this.syncSampleFiltersFromRoute(this.$route);

    if (this.wellName) {
      this.fetchData();
    } else {
      this.data = [];
      this.allMappedSamples = [];
      this.totalItems = 0;
    }
  },
};
</script>

<style scoped>
@import '../../assets/Styling/qaqc.css';

.detail-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.back-btn {
  margin-right: 16px;
}

.qa-tabs {
  margin-top: 16px;
  margin-bottom: 16px;
}

.qa-tabs .v-tab {
  font-size: 16px;
  font-weight: 500;
  text-transform: none;
  min-height: 30px;
}

.qa-tabs .v-tab--selected {
  font-weight: 600;
}

.cursor-pointer {
  cursor: pointer;
}

.clickable-cell:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.no-asset-div {
  padding: 48px 16px;
  text-align: center;
}

.no-asset-div h4 {
  margin: 16px 0 8px 0;
  color: #666;
}

.no-asset-div p {
  color: #999;
  margin: 0;
}

.timeout {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
}

.qaqc-main-container {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.qaqc-detail-pagination-wrap .qaqc-pagination-container {
  margin-top: 0;
}

.qaqc-top-indicators-with-context {
  justify-content: space-between !important;
  gap: 12px;
  flex-wrap: wrap;
}

.qaqc-top-indicators-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 24px;
  min-width: 0;
}

.qaqc-context-block {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px 24px;
}

.qaqc-context-pair {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.qaqc-context-title {
  font-size: 14px;
  font-weight: 600;
  color: #fff;
}

.qaqc-context-value {
  font-size: 14px;
  font-weight: 600;
  color: #fff;
}

.qaqc-top-indicators-legend {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.qaqc-table-section {
  width: 100%;
  overflow-x: auto;
}

.qaqc-detail-table-card {
  background-color: #ffffff;
}

.qaqc-detail-table-card :deep(.qaqc-table-wrap) {
  margin: 0;
}

.qaqc-detail-samples-table {
  width: 100%;
  min-width: 960px;
}

.qaqc-detail-samples-table :deep(.v-table__wrapper) {
  overflow-x: auto;
  overflow-y: visible;
}

.qaqc-detail-samples-table :deep(tbody tr:nth-child(even)) {
  background-color: #f3f4f6;
}

.qaqc-detail-samples-table :deep(tbody tr:nth-child(odd)) {
  background-color: #ffffff;
}

.qaqc-detail-samples-table :deep(tbody tr:hover) {
  background-color: #e5e7eb !important;
}

.qaqc-detail-samples-table :deep(td) {
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal;
}

.qaqc-detail-body-cell {
  padding: 10px 16px !important;
  vertical-align: middle;
  font-size: 14px;
  color: #374151;
}


.qaqc-detail-tests-cell {
  min-width: 220px;
  max-width: 400px;
}

.qaqc-detail-samples-table :deep(.qaqc-tests-container) {
  gap: 6px;
}

.qaqc-detail-samples-table :deep(thead th) {
  text-align: left !important;
  padding: 10px 16px !important;
  font-size: 13.5px;
  font-weight: 600 !important;
  letter-spacing: 0.1px;
}

.dark-mode .qaqc-detail-table-card {
  background-color: var(--dark-mode-bg-elevated, #1e1e1e);
}

.dark-mode .qaqc-detail-samples-table :deep(tbody tr:nth-child(even)) {
  background-color: rgba(255, 255, 255, 0.04);
}

.dark-mode .qaqc-detail-samples-table :deep(tbody tr:nth-child(odd)) {
  background-color: transparent;
}

.dark-mode .qaqc-detail-body-cell {
  color: rgba(255, 255, 255, 0.87);
}

.dark-mode .qaqc-detail-fluid-chip {
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.85);
}
</style>
